import { ArrowLeft, ArrowRight } from 'lucide-react';
import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppContext } from '@/app/providers/AppContextProvider';
import { Button } from '@/components/ui/button';
import { useFlowHighlightClass } from '@/hooks/useFlowGuidance';
import { cn } from '@/lib/utils';

interface NavigationProps {
	step: number;
	setStep: (step: number) => void;
	nextButtonText?: string;
	nextButtonArrow?: boolean;
	previousButtonText?: string;
	previousButtonArrow?: boolean;
	previousButtonDisabled?: boolean;
	nextButtonDisabled?: boolean;
	isStepValid?: boolean;
	isSaving: boolean;
	setIsSaving: (saving: boolean) => void;
}

const Navigation = ({
	step,
	setStep,
	nextButtonText,
	nextButtonArrow = true,
	previousButtonText,
	previousButtonArrow = false,
	previousButtonDisabled = false,
	nextButtonDisabled = false,
	isStepValid = true,
	isSaving,
	setIsSaving
}: NavigationProps) => {
	const { t } = useTranslation();
	const { id, selected } = useParams<{ id: string; selected?: string }>();
	const navigate = useNavigate();
	const { applicationData, applicationManagement } = useAppContext();

	const defaultNextText = nextButtonText || t('application.navigation.next');
	const defaultPreviousText = previousButtonText || t('application.navigation.previous');

	const applicationDataRef = useRef(applicationData);

	useEffect(() => {
		applicationDataRef.current = applicationData;
	}, [applicationData]);

	const goToNextStep = useCallback(async (): Promise<void> => {
		if (!id) return;

		setIsSaving(true);

		let payload = applicationDataRef.current;

		// Ensure selected quote from URL is always included so we send the correct
		// value even if a late fetchApplication overwrote state with null
		if (selected) {
			payload = {
				...payload,
				selected_sick_leave_short_term_quote_id: selected
			};
		}

		try {
			await applicationManagement.save(id, payload);
			if (step === 4) await applicationManagement.submit(id);

			setStep(step + 1);
		} catch (err) {
			console.error('Error saving application data:', err);
		} finally {
			setIsSaving(false);
		}
	}, [id, step, selected, applicationManagement, setStep, setIsSaving]);

	useEffect(() => {
		const handleKeyPress = (e: KeyboardEvent) =>
			e.key === 'Enter' && !nextButtonDisabled && isStepValid && !isSaving && goToNextStep();
		window.addEventListener('keypress', handleKeyPress);
		return () => window.removeEventListener('keypress', handleKeyPress);
	}, [nextButtonDisabled, isStepValid, isSaving, goToNextStep]);

	const goToPreviousStep = () => {
		if (step > 1) {
			setStep(step - 1);
			return;
		}

		const selectedQuoteId =
			selected || applicationDataRef.current.selected_sick_leave_short_term_quote_id;
		navigate(selectedQuoteId ? `/aanvulling/${id}/${selectedQuoteId}` : `/aanbod/${id}`);
	};

	const isDisabled = nextButtonDisabled || !isStepValid || isSaving;
	// Highlight the advance button as the flow's next action, but only while it can
	// actually be clicked — never lure the visitor toward a disabled (invalid) step.
	const nextHighlight = useFlowHighlightClass('application-next', !isDisabled);

	return (
		<div className="text-center py-2 lg:py-10 flex flex-col-reverse sm:flex-row sm:justify-center gap-2 sm:gap-0">
			<Button
				type="button"
				variant="outline"
				size="xl"
				className="font-bold w-full sm:w-auto sm:mr-3 lg:mr-4"
				onClick={goToPreviousStep}
				disabled={previousButtonDisabled}
				icon={previousButtonArrow ? <ArrowLeft className="w-4 h-4" /> : undefined}
				data-testid="application-previous-button"
			>
				{defaultPreviousText}
			</Button>
			<Button
				type="button"
				variant="default"
				size="xl"
				className={cn('font-bold w-full sm:w-auto', nextHighlight)}
				onClick={goToNextStep}
				disabled={isDisabled}
				loading={isSaving}
				iconRight={nextButtonArrow ? <ArrowRight className="w-4 h-4" /> : undefined}
				data-testid="application-next-button"
			>
				{defaultNextText}
			</Button>
		</div>
	);
};

export default Navigation;
